import { useToast } from "~/composables/useToast";
import type { ProfessionalCardPhotoApi } from "./professional-card-photo-field.model";

/**
 * Uploads the talent's carte BTP photo, from the contract form, the creation form or the request page, with its
 * toasts and loading state. The photo is stored at once; `onUploaded` lets the caller reload what it shows.
 */
export const useProfessionalCardPhotoUpload = (
  getTalentId: () => string,
  {
    uploadProfessionalCardPhoto,
    onUploaded,
  }: {
    uploadProfessionalCardPhoto: ProfessionalCardPhotoApi["uploadProfessionalCardPhoto"];
    onUploaded: () => Promise<void>;
  }
) => {
  const { t } = useI18n();
  const { addToastError, addToastSuccess } = useToast();

  const isUploadingPhoto = ref(false);

  const uploadPhoto = async (photo: File) => {
    if (isUploadingPhoto.value) return;
    isUploadingPhoto.value = true;
    try {
      await uploadProfessionalCardPhoto(getTalentId(), photo);
      addToastSuccess({
        title: t("professionalCardRequests.complianceResolution.pendingRequest.toasts.photoUploaded"),
      });
      await onUploaded();
    } catch (error) {
      addToastError(
        { title: t("professionalCardRequests.complianceResolution.pendingRequest.toasts.photoUploadFailed") },
        error
      );
    } finally {
      isUploadingPhoto.value = false;
    }
  };

  return { isUploadingPhoto, uploadPhoto };
};
